import { useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useUsersList } from 'hooks/api/use-users';

import * as reportAccessService from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment.helpers';

export interface ReportData {
  id: string | number;
  title?: string;
  hasDataIssue?: boolean;
  autoAssignToUsers?: boolean;
  requiredForAllUsers?: boolean;
  userAssignmentStatus?: string;
  [key: string]: unknown;
}

export interface ReportUser {
  id: string | number;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  hasAccess?: boolean;
  [key: string]: unknown;
}

interface UseReportDataResult {
  reports: ReportData[];
  setReports: Dispatch<SetStateAction<ReportData[]>>;
  users: ReportUser[];
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  userId: string | undefined;
}

export const useReportData = (
  clientId: string | number | undefined,
  productId: string | number | undefined
): UseReportDataResult => {
  const [reports, setReports] = useState<ReportData[]>([]);
  const [users, setUsers] = useState<ReportUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;

  const { data: usersData, loading: usersLoading } = useUsersList({
    pageSize: 100,
    filters: { clientId: clientId as string | number },
    enabled: !!userId && !!clientId && !!productId,
  });

  useEffect(() => {
    if (usersData.length > 0) {
      setUsers(usersData as ReportUser[]);
    }
  }, [usersData]);

  useEffect(() => {
    if (!userId || !clientId || !productId) return;

    const fetchReports = async (): Promise<void> => {
      setLoading(true);
      try {
        const reportsPayload = await reportAccessService.fetchClientReports({
          userId,
          clientId,
          productId,
        });

        if (Array.isArray(reportsPayload)) {
          setReports(reportsPayload as ReportData[]);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [userId, clientId, productId]);

  return {
    reports,
    setReports,
    users,
    loading: loading || usersLoading,
    setLoading,
    userId,
  };
};

export default useReportData;
